<template>
    <BaseLink
        v-if="props.game"
        :class="$style.container"
        v-bind="linkData"
    >
        <div
            ref="containerRef"
            :class="['sqrcl', $style.containerCard]"
        >
            <CardContainer
                appearance="vertical"
                :class="$style.gameCard_override"
                :alt="additionalGameData.title"
                :src="topPlaygamaIcons[props.game?.hru as string]"
                responsive
                lazy
                :sqrcl="false"
                itemscope
                itemtype="https://schema.org/VideoGame"
                itemprop="image"
                data-interface-target="game"
                @mouseenter="setMouseOver"
                @mouseleave="setMouseOut"
                @focus="setMouseOver"
                @blur="setMouseOut"
            >
                <svg
                    v-if="props.game && isMounted"
                    id="svgTitle"
                    viewBox="0 0 220 391"
                    xmlns="http://www.w3.org/2000/svg"
                    :data-pg-top="true"
                    data-allow-mismatch
                    :class="[$style.svg, animationFrameId ? $style.svgOnAutoBlink : '', 'lazyload']"
                >
                    <title>{{ additionalGameData.title }}</title>
                    <defs>
                        <filter :id="`${svgNamespace}-brightnessFilter`">
                            <feColorMatrix
                                in="SourceGraphic"
                                type="luminanceToAlpha"
                                result="luma"
                            />

                            <feComponentTransfer
                                in="luma"
                                result="mask"
                            >
                                <feFuncA
                                    type="table"
                                    :tableValues="`${generateTableValues10}`"
                                />
                            </feComponentTransfer>

                            <feFlood
                                :flood-color="`${additionalGameData.color}`"
                                result="color"
                            />

                            <feComposite
                                in="color"
                                in2="mask"
                                operator="in"
                                result="colored"
                            />

                            <!--WHITE thin-->
                            <feComponentTransfer
                                in="luma"
                                result="mask-thin"
                            >
                                <feFuncA
                                    type="table"
                                    :tableValues="`${generateTableValues1}`"
                                />
                            </feComponentTransfer>

                            <feFlood
                                flood-color="#ffffff88"
                                result="color"
                            />
                            <feComposite
                                in="color"
                                in2="mask-thin"
                                operator="in"
                                result="white-thin"
                            />

                            <feColorMatrix
                                in="white-thin"
                                type="matrix"
                                values="
          1 0 0 0 0
          0 1 0 0 0
          0 0 1 0 0
          0 0 0 1 0"
                                result="white-thin-glowSource"
                            />

                            <feGaussianBlur
                                in="white-thin-glowSource"
                                stdDeviation="1.1"
                                result="white-thin-blurredGlow"
                            />

                            <!--WHITE thin end-->

                            <feColorMatrix
                                in="colored"
                                type="matrix"
                                values="
          1 0 0 0 0
          0 1 0 0 0
          0 0 1 0 0
          0 0 0 1 0"
                                result="glowSource"
                            />

                            <feGaussianBlur
                                in="glowSource"
                                stdDeviation="10"
                                result="blurredGlow"
                            />

                            <feMerge>
                                <feMergeNode in="blurredGlow" />
                                <feMergeNode in="blurredGlow" />
                                <feMergeNode in="white-thin-blurredGlow" />
                                <feMergeNode in="white-thin-blurredGlow" />
                            </feMerge>
                        </filter>
                    </defs>
                    <image
                        :data-href="`${topPlaygamaIcons[props.game.hru]}`"
                        width="100%"
                        height="100%"
                        preserveAspectRatio="xMidYMid slice"
                    />
                    <image
                        :data-href="`${topPlaygamaIcons[props.game.hru + '_fod']}`"
                        width="100%"
                        height="100%"
                        :filter="`url(#${svgNamespace}-brightnessFilter)`"
                        preserveAspectRatio="xMidYMid slice"
                    />
                </svg>
            </CardContainer>

            <GameCardTitleWithIcon
                v-if="props.game"
                :icon="additionalGameData.icon"
                :likes="additionalGameData.likes"
                :title="additionalGameData.title"
            />
        </div>
        <Badge
            v-if="props.game"
            :badges="additionalGameData.badges"
            :isHide="isMouseOver"
        />
    </BaseLink>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, useId, watch } from 'vue'

import Badge from '@/components/Badge/Badge.vue'
import BaseLink from '@/components/BaseLink.vue'
import CardContainer from '@/components/CardContainer.vue'
import { useGetExternalUrl } from '@/composables/url-helpers'
import { ROUTE_NAMES } from '@/router/constants'
import { useAppStore } from '@/store/app-store'
import type { LinkTarget, ShortGameTypePlaygamaTop } from '@/types'
import { useIsMounted } from '@/utils/vue-hooks/use-is-mounted'
import { useOnScreen } from '@/utils/vue-hooks/use-on-screen'

import GameCardTitleWithIcon from './GameCardTitleWithIcon.vue'
import { topPlaygamaIcons } from './images'
import { topGames } from './prepare-top-playgama'

const props = defineProps<{
    game?: ShortGameTypePlaygamaTop
    gameLinkTarget: LinkTarget | undefined
}>()

const appStore = useAppStore()

const getExternalUrl = useGetExternalUrl()

const isMounted = useIsMounted()
const additionalGameData = computed(() => {
    const data = topGames.find((topGame) => topGame.hru === props.game?.hru)

    return {
        color: data?.color || '',
        likes: data?.likes || '',
        icon: data?.icon || '',
        title: data?.title || '',
        badges: data?.badges || [],
    }
})

const linkData = computed(() => {
    const to = props.game && { name: ROUTE_NAMES.GAME_PAGE, params: { game: props.game.hru } }
    return appStore.isPWA ? { to } : { externalTo: to && getExternalUrl(to), target: props.gameLinkTarget }
})

const containerRef = ref<HTMLDivElement>()
const { isVisible } = useOnScreen(containerRef, 0.5)

const generateTableValues = (centerSize: number = 5, y?: number): string => {
    const size = 100
    const yPosition = 1 - (y ? y : 0.5)
    const clampedPosition = Math.max(0, Math.min(1, yPosition))
    const peakCenter = clampedPosition * (size - 1)
    const resultArray: number[] = new Array(size).fill(0)

    const min = Math.floor(Math.max(peakCenter - centerSize, 0))
    const max = Math.floor(Math.min(peakCenter + centerSize, size - 1))
    for (let i = min; i < max; i++) {
        resultArray[i] = 1
    }
    return resultArray.join(' ')
}

const generateTableValues1 = computed(() => {
    if (animationFrameId.value) {
        return generateTableValues(1, autoBlickPosition.value)
    }
    if (mousePosition.value === 0) {
        return [0]
    }
    return generateTableValues(1, mousePosition.value)
})

const generateTableValues10 = computed(() => {
    if (animationFrameId.value) {
        return generateTableValues(10, autoBlickPosition.value)
    }
    if (mousePosition.value === 0) {
        return [0]
    }
    return generateTableValues(10, mousePosition.value)
})

const svgNamespace = useId()
const mousePosition = ref<number>()
const isMouseOver = ref(false)
const autoBlickPosition = ref<number>(0)
const delta = ref<number>(0)
const animationFrameId = ref<number | undefined>(undefined)

function mouseMove(e: MouseEvent) {
    if (!animationFrameId.value) {
        const rect = (e.target as HTMLElement).getBoundingClientRect()
        const y = e.clientY - rect.top
        mousePosition.value = (y / rect.height) * 0.8
    }
}

function setMouseOver() {
    isMouseOver.value = true
    containerRef.value?.addEventListener('mousemove', mouseMove)
}

function setMouseOut() {
    isMouseOver.value = false
    containerRef.value?.removeEventListener('mousemove', mouseMove)
}

function startAutoBlink() {
    if (!animationFrameId.value) {
        autoBlickPosition.value = 0
        delta.value = 0.035

        const animate = () => {
            autoBlickPosition.value += delta.value
            if (autoBlickPosition.value >= 0.8) {
                delta.value = -1 * delta.value
            } else if (autoBlickPosition.value <= 0) {
                mousePosition.value = 0
                if (animationFrameId.value) {
                    cancelAnimationFrame(animationFrameId.value)
                }
                animationFrameId.value = undefined
                return
            }
            animationFrameId.value = requestAnimationFrame(animate)
        }

        animate()
    }
}

onUnmounted(() => {
    if (animationFrameId.value) {
        cancelAnimationFrame(animationFrameId.value)
    }
    containerRef.value?.removeEventListener('mousemove', mouseMove)
})

watch(isVisible, (newValue) => {
    if (newValue && window?.innerWidth <= 1200) {
        startAutoBlink()
    }
})
</script>

<style module>
.svg {
    grid-area: main;
    width: 100%;
    height: 100%;
    opacity: 0;
    transition:
        opacity 0.3s ease,
        transform 0.5s ease;
}

.svgOnAutoBlink {
    opacity: 1;
}

.gameCard_override {
    transition: transform 0.5s ease;
    transform: scale(1);
}

.container {
    position: relative;
}

.containerCard {
    position: relative;
    border-radius: 12px;
    overflow: hidden;
    --sqrcl: clamp(0rem, 30%, 50rem);
}

.containerCard:hover .gameCard_override {
    transform: scale(1.03);
}

.containerCard:hover .svg {
    opacity: 1;
    transition: transform 0.5s ease;
    transform: scale(1.03);
}
</style>
